import { useField } from '@unform/core'
import { Subtitle1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { FormGroup, ErrorMessage } from 'components/elements/Inputs/Input.css'
import SelectArrowIcon from 'assets/icons/select-arrow.svg'

const Wrapper = styled.div<{ isDarkTheme?: boolean }>`
    position: relative;

    & .fake-input {
        user-select: none;
        padding: 20px 15px !important;

        p {
            margin-bottom: 0px;
            padding: 0;
            font-family: var(--font-inter);
            color: ${(props) => (props.isDarkTheme ? theme.colors.white : theme.colors.placeholderText)};
            line-height: 22px;
        }
        .placeholder {
            color: ${(props) => (props.isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.caption)};
            font-size: 18px;
        }
    }
`

const Text = styled.p`
    && {
        color: ${theme.colors.black};
    }
`

const Placeholder = styled.p`
    && {
        color: ${theme.colors.body1Black};
    }
`

const SelectArrow = styled(SelectArrowIcon)`
    position: absolute;
    right: 10px;
    top: 45%;
`

interface Props<T, E> {
    name: string
    placeholder: string
    label?: string
    noMargin?: boolean

    value: T
    onClick(): void
    onUpdate(value: E): void
    selectedPreview: string
    isDarkTheme?: boolean
}

const GenericFormElement = <T, E>(props: Props<T, E>) => {
    const fakeInputRef = useRef<HTMLDivElement>(null)
    const { fieldName, defaultValue, registerField, error } = useField(props.name)
    const isError = !!error

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: undefined,
            path: undefined,
            getValue: () => {
                return props.value
            },
        })
    }, [fieldName, registerField, props.value, props.onUpdate])

    useEffect(() => {
        if (defaultValue) {
            props.onUpdate(defaultValue)
        }
    }, [defaultValue])

    return (
        <FormGroup noMargin isError={isError} darkTheme={props.isDarkTheme}>
            <label>
                <Subtitle1 color={theme.colors.body2Black}>{props.label}</Subtitle1>
            </label>
            <Wrapper isDarkTheme={props.isDarkTheme}>
                <SelectArrow />
                <div ref={fakeInputRef} className="fake-input" tabIndex={0} onClick={props.onClick} data-id={props.name}>
                    {props.selectedPreview ? (
                        <Text>{props.selectedPreview}</Text>
                    ) : (
                        <Placeholder>{props.placeholder}</Placeholder>
                    )}
                </div>
            </Wrapper>

            <ErrorMessage>{error}</ErrorMessage>
        </FormGroup>
    )
}

export default GenericFormElement
